import { allComponents, provideFASTDesignSystem } from '@microsoft/fast-components';
import { FASTRouter } from '@microsoft/fast-router';
import { logger } from '../utils';
import {
  provideDesignSystem,
  zeroBadge,
  zeroButton,
  zeroCard,
  zeroCheckbox,
  zeroCombobox,
  zeroDesignSystemProvider,
  zeroRadio,
  zeroSegmentedControl,
  zeroSlider,
  zeroTab,
  zeroTabs,
  zeroTextField,
  zeroNumberField,
  zeroSegmentedItem,
  zeroToast,
  zeroSelect,
  zeroModal,
  zeroDialog,
} from '@genesislcap/foundation-zero';

import { zeroGridComponents, zeroGridPro } from '@genesislcap/foundation-zero-grid-pro';
import { LicenseManager } from '@ag-grid-enterprise/core';
import { SideBarModule } from '@ag-grid-enterprise/side-bar';
import { FiltersToolPanelModule } from '@ag-grid-enterprise/filter-tool-panel';
import { ColumnsToolPanelModule } from '@ag-grid-enterprise/column-tool-panel';
import { SetFilterModule } from '@ag-grid-enterprise/set-filter';
import { MenuModule } from '@ag-grid-enterprise/menu';
import { ModuleRegistry } from '@ag-grid-community/core';

import { customTabStyles } from './custom-styles/zero-tab';
import { customButtonStyles } from './custom-styles/zero-button';
import { TabButtons } from './common/tab-buttons/tab-buttons';
import { CustomHeader } from './header';
import { ProjectModal } from './page-specific/project-modal/project-modal';
import { RemodelOperationalProject } from './page-specific/project-modal/remodel-operational-projects/remodel-operational-projects';
import { GeneralTab } from './page-specific/project-modal/tabs/general/general-tab';
import { RightSideModal } from './right-side-modal/right-side-modal';
import { CenteredModal } from './centered-modal/centered-modal';
import { LocationTab } from './page-specific/project-modal/tabs/location/location-tab';
import { MarketInfoTab } from './page-specific/project-modal/tabs/market-info/market-info-tab';
import { ContractsTab } from './page-specific/project-modal/tabs/contracts-summaries/contracts-summaries';
import { ConsultantTab } from './page-specific/project-modal/tabs/consultant-reports/consultant-reports';
import { PermitsTab } from './page-specific/project-modal/tabs/permits-government-approvals/permits-government-approvals';
import { customAgGridStyles } from './custom-styles/ag-grid';
import { ProjectCard } from './common/project-card/project-card';
import { ProjectVppaCard } from './common/project-vppa-card/project-vppa-card';
import { WatchlistProjectCard } from './common/watchlist-project-card/watchlist-project-card';
import { MyProjectsContainer } from './page-specific/seller-dashboard-components/my-projects-container/my-projects';
import { WatchlistContainer } from './page-specific/buyer-dashboard-components/watchlist-container/watchlist';
import { SellerTile } from './page-specific/seller-dashboard-components/seller-tile/seller-tile';
import { TileCard } from './common/tile-cards/tile-card';
import { VppaManagement } from './page-specific/seller-dashboard-components/vppa-management/vppa-management';
import { MyPortfolio } from './page-specific/buyer-dashboard-components/my-portfolio/my-portfolio';
import { VppaModal } from './modals/vppa-modal/vppa-modal';
import { FileUpload } from './common/file-upload/file-upload';
import { ValidationGrid } from './common/validation-grid/validation-grid';
import { ReducedFileUpload } from './common/reduced-file-upload/reduced-file-upload';
import { VppaForm } from './modals/vppa-modal/vppa-form/vppa-form';
import { UploadValidation } from './page-specific/project-modal/upload-validation/upload-validation';
import { ErrorUploadGrid } from './common/error-upload-grid/error-upload-grid';
import { MultiSelect } from './common/multi-select';
import { AllVppas } from './page-specific/seller-dashboard-components/all-vppas/all-vppas';
import { RfqModal } from './modals/rfq-modal/rfq-modal';
import { RfqForm } from './modals/rfq-modal/rfq-form/rfq-form';
import { AllRfq } from './page-specific/buyer-dashboard-components/all-rfq/all-rfq';
import { StructuredChatModal } from './modals/structured-chat/structured-chat';
import { UserModal } from './modals/user-modal/user-modal';
import { RfqDetailModal } from './modals/rfq-detail-modal/rfq-detail-modal';
import { VppaDetailModal } from './modals/vppa-detail-modal/vppa-detail-modal';
import { CancelModal } from './modals/cancel-modal/cancel-modal';
import { IndividualProjectVppas } from './page-specific/individual-project-components/individual-projects-vppas/individual-projects-vppas';
import { BuyerInterest } from './page-specific/seller-dashboard-components/buyer-interest/buyer-interest';
import { customSegmentedControlStyles } from './custom-styles/zero-segmented-control';
import { customSliderStyles } from './custom-styles/zero-slider';
import { customTooltip, customTooltipStyles } from './custom-styles/zero-tooltip';
import { SummaryChat } from './modals/structured-chat/summary-chat/summary-chat';
import { MarketDepthQuery } from './page-specific/market-depth/market-depth-query/market-depth-query';
import { MarketDepthGrid } from './page-specific/market-depth/market-depth-grid/market-depth-grid';
import { customBadgeStyles } from './custom-styles/zero-badge';
import { customCardStyles } from './custom-styles/zero-card';
import { customZeroComboboxStyles } from './custom-styles/zero-combobox';
import { SideMenu } from './common/side-menu/side-menu';
import { RFQSelector } from './modals/structured-chat/rfq-selector/rfq-selector';
import { ClosePopup } from './right-side-modal/close-popup/close-popup';
import { MarketDepthVppaGrid } from './page-specific/market-depth/market-depth-vppa-grid/market-depth-vppa-grid';
import { NegotiationTile } from './modals/structured-chat/negotiation-tile/negotiation-tile';
import { AddRFQ } from './modals/structured-chat/add-rfq/add-rfq';
import { customCheckboxStyles } from './custom-styles/zero-checkbox';
import { IndividualProjectsOverview } from './page-specific/individual-project-components/individual-projects-overview/individual-projects-overview';
import { RfqListing } from './page-specific/seller-rfq-components/rfq-listing/rfq-listing';
import { SellerRfqQuery } from './page-specific/seller-rfq-components/seller-rfq-query/seller-rfq-query';
import { SellerInterest } from './page-specific/buyer-dashboard-components/seller-interest/seller-interest';
import { ProjectListing } from './page-specific/seller-rfq-components/project-listing/project-listing';
import { customZeroRadioStyles } from './custom-styles/zero-radio';
import { BasketListing } from './page-specific/seller-rfq-components/basket-listing/basket-listing';
import { IndividualProjectsDrilldown } from './page-specific/individual-project-components/individual-projects-drilldown/individual-projects-drilldown';
import { VppaListing } from './page-specific/seller-rfq-components/vppa-listing/vppa-listing';
import { customTextFieldStyles } from './custom-styles/zero-text-field';
import { customNumberFieldStyles } from './custom-styles/zero-number-field';
import { customTabsStyles } from './custom-styles/zero-tabs';
import { ExecutionCard } from './page-specific/execution-components/execution-card';
import { ExecutionDrilldown } from './page-specific/execution-components/execution-drilldown/execution-drilldown';
import { MoreInfoModal } from './common/more-info/more-info-modal/more-info-modal';
import { MoreInfoIcon } from './common/more-info/more-info-icon/more-info-icon';
import { KnowledgeBlockModal } from './modals/knowledge-block-modal/knowledge-block-modal';
import { InfoIcon } from './common/info-icon/info-icon';
import { IndividualProjectGraphs } from './page-specific/individual-project-components/individual-projects-graphs/individual-projects-graphs';
import { Generation } from './page-specific/individual-project-components/graphs/generation/generation';
import { LmeTotalCarbonReduction } from './page-specific/individual-project-components/graphs/lme-total-carbon-reduction/lme-total-carbon-reduction';
import { CarbonFreeEnergy } from './page-specific/individual-project-components/graphs/carbon-free-energy/carbon-free-energy';
import { NodalBasis } from './page-specific/individual-project-components/graphs/nodal-basis/nodal-basis';
import { TimerBar } from './common/timer-bar/timer-bar';
import { SettlementForecast } from './page-specific/individual-project-components/graphs/settlement-forecast/settlement-forecast';
import { MultiSegmentedControl } from './common/multi-segmented-control/multi-segmented-control';
import { FloatingPrice } from './page-specific/individual-project-components/graphs/floating-price/floating-price';
import { ImpliedCarbonPrice } from './page-specific/individual-project-components/graphs/implied-carbon-price/implied-carbon-price';
import { RecImpliedCarbonPrice } from './page-specific/individual-project-components/graphs/rec-implied-carbon-price/rec-implied-carbon-price';
import { ProjectRfqsGraph } from './page-specific/seller-rfq-components/project-rfqs-graph/project-rfqs-graph';
import { customSegmentedItemStyles } from './custom-styles/zero-segmented-item';
import { EditCompany } from './mf/companys/edit';
import { Companys } from './mf/companys/company';
import { NegotiationsGrid } from './common/negotiations-grid/negotiations-grid';
import { AutoExecuteApprovedCounterpartiesModal } from './modals/auto-execute-approved-counterparties-modal/auto-execute-approved-counterparties-modal';
import { CompanyApproval } from './common/company-approval/company-approval';
import { customToastStyles } from './custom-styles/zero-toast';
import { ProjectMetrics } from './page-specific/individual-project-components/individual-projects-drilldown/project-metrics/project-metrics';
import { AuditGrid } from './page-specific/user-management/audit-grid/audit-grid';
import { MarketPerformanceGraph } from './common/graph/market-performance-graph/market-performance-graph';
import { AnalyticsWizard } from './page-specific/buyer-analytics-components/analytics-wizard/analytics-wizard';
import { AnalyticsStepOne } from './page-specific/buyer-analytics-components/analytics-step-one/analytics-step-one';
import { AnalyticsStepTwo } from './page-specific/buyer-analytics-components/analytics-step-two/analytics-step-two';
import { AnalyticsStepThree } from './page-specific/buyer-analytics-components/analytics-step-three/analytics-step-three';
import { AllContractsGraph } from './page-specific/buyer-analytics-components/analytics-step-one/all-contracts-graph/all-contracts-graph';
import { QuadrantGrid } from './page-specific/buyer-analytics-components/analytics-step-one/quadrant-grid/quadrant-grid';
import { QuadrantSpiderChart } from './page-specific/buyer-analytics-components/analytics-step-one/quadrant-spider-chart/quadrant-spider-chart';
import { MultiselectDatasource, OptionsDatasource } from '@genesislcap/foundation-ui';
import { IndividualProjectVppasGrid } from './page-specific/individual-project-components/individual-projects-vppas-grid/individual-projects-vppas-grid';
import { MetricTile } from './page-specific/individual-project-components/individual-projects-drilldown/metrics-tile/metrics-tile';
import { DrilldownFloatingPriceGraph } from './page-specific/buyer-analytics-components/analytics-step-two/drilldown-floating-price-graph/drilldown-floating-price-graph';
import { DrilldownImpliedCarbonGraph } from './page-specific/buyer-analytics-components/analytics-step-two/drilldown-implied-carbon-graph/drilldown-implied-carbon-graph';
import { DrilldownNodalLmeGraph } from './page-specific/buyer-analytics-components/analytics-step-two/drilldown-nodal-lme-graph/drilldown-nodal-lme-graph';
import { DrilldownExpectedSettlementGraph } from './page-specific/buyer-analytics-components/analytics-step-two/drilldown-expected-settlement-graph/drilldown-expected-settlement-graph';
import { ThirdParty } from './mf/third-party/third-party';
import { LinkAdvisorsCompany } from './mf/companys/link-advisors/link-advisors';
import { customSelectStyles } from './custom-styles/zero-select';
import { customModalStyles } from './custom-styles/zero-modal';
import { AssignedUsers } from './mf/companys/edit/assigned-users/assigned-users';
import { AutoFillInput } from './common/auto-fill-input/auto-fill-input';
import { NegotiationDetailModal } from './modals/negotiation-detail-modal/negotiation-detail-modal';
import { customDialogStyles } from './custom-styles/zero-dialog';
import { CascadeUserManagement } from './mf/user/cascade-user';
import { EditUserModal } from './mf/user/edit/edit-user-modal';
import { LandingPage } from './page-specific/landing-page/landing-page';
import { SefAgreementRequired } from './header/sef-agreement-required/sef-agreement-required';

MultiselectDatasource;
OptionsDatasource;
/**
 * Overriding styles for zero components
 */
provideDesignSystem().register(
  zeroDesignSystemProvider(),
  zeroTab({
    styles: customTabStyles,
  }),
  zeroTabs({
    styles: customTabsStyles,
  }),
  zeroButton({
    styles: customButtonStyles,
  }),
  zeroSegmentedControl({
    styles: customSegmentedControlStyles,
  }),
  zeroSegmentedItem({
    styles: customSegmentedItemStyles,
  }),
  zeroSlider({
    styles: customSliderStyles,
  }),
  zeroBadge({
    styles: customBadgeStyles,
  }),
  zeroCard({
    styles: customCardStyles,
  }),
  zeroGridPro({
    styles: customAgGridStyles,
  }),
  zeroCombobox({
    styles: customZeroComboboxStyles,
  }),
  zeroCheckbox({
    styles: customCheckboxStyles,
  }),
  zeroRadio({
    styles: customZeroRadioStyles,
  }),
  zeroTextField({
    styles: customTextFieldStyles,
  }),
  zeroNumberField({
    styles: customNumberFieldStyles,
  }),
  zeroToast({
    styles: customToastStyles,
  }),
  zeroModal({
    styles: customModalStyles,
  }),
  zeroDialog({
    styles: customDialogStyles,
  }),
  zeroSelect({
    styles: customSelectStyles,
  }),
  customTooltip({
      styles: customTooltipStyles
  })
);

// Registering AG Grid enterprise components as part of 5.0 upgrade
ModuleRegistry.registerModules([ SideBarModule, ColumnsToolPanelModule, FiltersToolPanelModule, SetFilterModule, MenuModule]);


LicenseManager.setLicenseKey(
  'CompanyName=GENESIS GLOBAL TECHNOLOGY LIMITED,LicensedGroup=Genesis Web,LicenseType=MultipleApplications,LicensedConcurrentDeveloperCount=3,LicensedProductionInstancesCount=4,AssetReference=AG-035099,SupportServicesEnd=8_January_2024_[v2]_MTcwNDY3MjAwMDAwMA==da697ff647e3d57eb719529bdf1506f0'
);

/**
 * New components
 */
provideFASTDesignSystem().register(
  allComponents,
  zeroGridComponents,
  zeroDesignSystemProvider(),
  /**
   * Cascade pattern
   */
  CustomHeader,
  RightSideModal,
  CenteredModal,
  SideMenu,
  /**
   * Modals
   */
  ProjectModal,
  RemodelOperationalProject,
  GeneralTab,
  LocationTab,
  MarketInfoTab,
  ContractsTab,
  ConsultantTab,
  PermitsTab,
  VppaModal,
  VppaForm,
  RfqModal,
  RfqForm,
  CancelModal,

  /**
   * Components for Explore route
   */
  // ExploreProjectContainer,
  // ExploreVPPAGrid,
  /**
   * Components for Seller Dashboard route
   */
  SellerTile,
  MyProjectsContainer,
  VppaManagement,
  AllVppas,
  BuyerInterest,
  NegotiationsGrid,
  /**
   * Components for Buyer dashboard route
   */
  MyPortfolio,
  AllRfq,
  WatchlistContainer,
  WatchlistProjectCard,
  SellerInterest,
  /**
   * Common components
   */
  TabButtons,
  ProjectCard,
  ProjectVppaCard,
  TileCard,
  FileUpload,
  ReducedFileUpload,
  ValidationGrid,
  UploadValidation,
  ErrorUploadGrid,
  MultiSelect,
  AutoFillInput,
  ClosePopup,
  TimerBar,
  MultiSegmentedControl,
  MarketPerformanceGraph,
  MoreInfoModal,
  MoreInfoIcon,
  KnowledgeBlockModal,
  InfoIcon,

  /**
   * Components for Individual Project Page
   */
  IndividualProjectVppas,
  IndividualProjectsOverview,
  IndividualProjectsDrilldown,
  IndividualProjectGraphs,
  Generation,
  LmeTotalCarbonReduction,
  CarbonFreeEnergy,
  NodalBasis,
  SettlementForecast,
  FloatingPrice,
  ImpliedCarbonPrice,
  RecImpliedCarbonPrice,
  ProjectMetrics,
  IndividualProjectVppasGrid,
  MetricTile,

  /**
   * Market Depth
   */
  MarketDepthQuery,
  MarketDepthGrid,
  MarketDepthVppaGrid,

  /**
   * Structured Chat
   */
  StructuredChatModal,
  SummaryChat,
  RFQSelector,
  NegotiationTile,
  AddRFQ,

  /**
   * Seller RFQ
   */
  RfqListing,
  SellerRfqQuery,
  ProjectListing,
  BasketListing,
  VppaListing,
  ProjectRfqsGraph,

  /**
   * RightMenu
   */
  UserModal,
  RfqDetailModal,
  VppaDetailModal,
  NegotiationDetailModal,

  /**
   * Done Alert Page (Execution Page)
   */
  ExecutionCard,
  ExecutionDrilldown,

  /**
   * Entity management
   */
  Companys,
  EditCompany,
  AssignedUsers,
  LinkAdvisorsCompany,
  AuditGrid,
  ThirdParty,
  CascadeUserManagement,
  EditUserModal,

  /**
   * Buyer Analytics
   */
  AnalyticsWizard,
  AnalyticsStepOne,
  AnalyticsStepTwo,
  AnalyticsStepThree,
  AllContractsGraph,
  QuadrantGrid,
  QuadrantSpiderChart,
  DrilldownFloatingPriceGraph,
  DrilldownImpliedCarbonGraph,
  DrilldownNodalLmeGraph,
  DrilldownExpectedSettlementGraph,

  /**
   * Landing Page
   */
  LandingPage,

  /**
   * Custom
   */
  AutoExecuteApprovedCounterpartiesModal,
  CompanyApproval,
  SefAgreementRequired
);

enum ResourceType {
  LOCAL = 'LOCAL',
  REMOTE = 'REMOTE',
}

/**
 * TODO: Think about sharing import functions across micro frontends.
 */
function loadZeroFallback() {
  return import(
    /* webpackMode: "lazy" */
    '@genesislcap/foundation-zero'
  );
}

/**
 * Granular
 */
async function loadZeroDesignSystem() {
  let type = ResourceType.REMOTE;
  try {
    // @ts-ignore
    return await import('foundationZero/ZeroDesignSystem');
  } catch (e) {
    type = ResourceType.LOCAL;
    return await loadZeroFallback();
  } finally {
    logger.debug(`Using '${type}' version of foundationZero/ZeroDesignSystem`);
  }
}

export type LoadRemotesOptions = {};

export async function loadRemotes() {
  const { registerZeroDesignSystem } = await loadZeroDesignSystem();
  return {
    ZeroDesignSystem: registerZeroDesignSystem(),
  };
}

/**
 * Ensure tree shaking doesn't remove these
 * Perhaps we keep these MFs style free, ie. they need to get wrapped to have style applied. Keeping it simple for now.
 */
FASTRouter;
